import type { AdminHouseholdDetail, SystemPresence } from "@/lib/api";

import { buildSheet } from "./sheet-data";
import { SheetStack } from "./sheet-stack";

/**
 * The kit sheet, for the dev-only `/preview` gallery.
 *
 * The states worth reviewing are the ones driven by an incomplete household
 * record: no resident name, no address, a home missing a system. Reproducing
 * those through the admin UI means editing a real household and putting it back
 * afterwards, so they get stubs here instead.
 *
 * This is a preview harness, not a second implementation: it renders the same
 * `SheetFront` and `SheetBack` off the same `buildSheet`, so anything that looks
 * right here is right on paper.
 */
export const KIT_SHEET_STAGES = [
  "personalised",
  "no-resident",
  "no-address",
  "no-heating",
  "no-air-quality",
  "hub-only",
  "no-access-code",
] as const;

type Stage = (typeof KIT_SHEET_STAGES)[number];

const BASE: AdminHouseholdDetail = {
  id: "preview",
  name: "BOI Office demo home",
  contact_email: "demo@boardofinnovation.com",
  contact_name: "Anna de Vries",
  address: "Kasteleinsstraat 12",
  postcode: "1000",
  city: "Brussels",
  country_code: "BE",
  access_code: "783236",
  preconfig: {} as AdminHouseholdDetail["preconfig"],
} as AdminHouseholdDetail;

function systems(flags: Partial<SystemPresence>): Pick<AdminHouseholdDetail, "preconfig"> {
  return { preconfig: { systems: flags } as AdminHouseholdDetail["preconfig"] };
}

const STAGE_HOUSEHOLDS: Record<Stage, AdminHouseholdDetail> = {
  personalised: BASE,
  "no-resident": { ...BASE, contact_name: null },
  "no-address": { ...BASE, address: null, postcode: null, city: null, country_code: null },
  "no-heating": { ...BASE, ...systems({ floor_heating: false }) },
  "no-air-quality": { ...BASE, ...systems({ air_quality: false }) },
  "hub-only": {
    ...BASE,
    ...systems({
      air_quality: false,
      roof_windows: false,
      floor_heating: false,
      ventilation: false,
    }),
  },
  "no-access-code": { ...BASE, access_code: null },
};

export function KitSheetPreview({ stage }: { stage: string }) {
  const household = STAGE_HOUSEHOLDS[stage as Stage] ?? BASE;
  const sheet = buildSheet(household, window.location.origin);

  return <SheetStack sheet={sheet} />;
}
